import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Question } from '../../../models/QuestionResponse';
import { ChatbotService, ApiError } from '../../../services/chatbot.service';
import { ChatError, ChatMessage, SimulationMessage, SimulationState } from '../../../models/SimulationMessage';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/User';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslationService } from '../../../services/translation.service';
import { MarkdownPipe } from '../../../pipes/markdown.pipe';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';

@Component({
  selector: 'q-simulation',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, MarkdownPipe, LottieComponent],
  templateUrl: './simulation.component.html',
  styleUrl: './simulation.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimulationComponent implements OnInit, OnDestroy {
  @Input() question!: Question;
  @Input() graphism!: string;

  /**
   * Event emitter that fires when the simulation is complete (FIN signal received)
   * Emits the final state of the simulation
   */
  @Output() simulationComplete = new EventEmitter<SimulationState>();
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef;

  user?: User;
  inputMessage = '';
  messages: ChatMessage[] = [];
  isLoading = false;
  isComplete = false;
  loadingProgress = 0;
  showProgressBar = false;
  loadingMessage = 'Traitement en cours';
  avatarAnimationOptions: AnimationOptions | null = {
    loop: false,
    autoplay: false,
  };
  private allMessages: SimulationMessage[] = [];
  private destroy$ = new Subject<void>();
  private finDetected = false;
  private streamingTimer: any = null;
  private progressTimer: any = null;

  constructor(
    private authService: AuthService,
    private chatbotService: ChatbotService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private translationService: TranslationService,
  ) {}

  async ngOnInit() {
    try {
      // Get user info for avatar display
      const user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded().pipe(takeUntil(this.destroy$)));
      this.user = user;

      this.avatarAnimationOptions = {
        ...this.avatarAnimationOptions,
        path: `assets/lottie/avatars/${this.user.avatar_id ?? 1}.json`,
      };
      // Initialize with the first message from the bot (question content)
      this.messages.push({
        content: this.question.question_content,
        isUser: false,
        timestamp: new Date(),
      });

      // Add the initial bot message to the allMessages array
      this.allMessages.push({
        role: 'assistant',
        content: this.question.question_content,
      });

      // Focus the input field
      this.focusInputField();

      // Trigger change detection
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error initializing simulation:', error);
      this.toastr.error(this.translationService.instant('questions.simulation.over'), this.translationService.instant('common.labeld.error'));
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clear any pending timers
    if (this.streamingTimer) {
      clearTimeout(this.streamingTimer);
    }

    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  /**
   * Focus the input field with a small delay
   * Public so it can be called from the template
   */
  focusInputField() {
    setTimeout(() => {
      if (this.messageInput && !this.isComplete && !this.isLoading) {
        this.messageInput.nativeElement.focus();
      }
    }, 100);
  }

  ngAfterViewInit() {
    this.scrollToBottom();
  }

  /**
   * Scrolls the message container to the bottom
   * @param immediate If true, scrolls immediately without animation
   */
  scrollToBottom(immediate = false) {
    if (immediate) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.forceScrollToBottom();
        });
      });
    } else {
      setTimeout(() => {
        if (this.scrollContainer) {
          const element = this.scrollContainer.nativeElement;
          element.scrollTo({
            top: element.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }

  /**
   * Force scroll to bottom using multiple methods to ensure it works
   */
  private forceScrollToBottom() {
    if (this.scrollContainer) {
      const element = this.scrollContainer.nativeElement;

      // Method 1: scrollTo
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'auto',
      });

      // Method 2: scrollTop (fallback)
      element.scrollTop = element.scrollHeight;

      // Method 3: scrollIntoView on last message (additional fallback)
      const lastMessage = element.lastElementChild;
      if (lastMessage) {
        lastMessage.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    }
  }

  /**
   * Listen for Enter key press on the document level
   * This ensures the Enter key works even when focus might be lost
   */
  @HostListener('document:keydown.enter', ['$event'])
  handleEnterKey(event: Event) {
    // Only handle if the target is the input field or the send button
    const target = event.target as HTMLElement;

    // Check if the simulation component is active and not in loading or complete state
    if (!this.isLoading && !this.isComplete) {
      // If the target is the input field or send button, or if the input field has focus
      if (target.classList.contains('simulation-input') || target.closest('.simulation-send-button') || document.activeElement === this.messageInput?.nativeElement) {
        // Prevent default to avoid form submission if inside a form
        event.preventDefault();

        // Only send if there's actual content
        if (this.inputMessage.trim()) {
          // Send the message
          this.sendMessage();
        } else {
          // Focus the input field if it's empty
          this.focusInputField();
        }
      }
    }
  }

  /**
   * Listen for Shift+Enter key combination to add a new line instead of sending
   * This allows for multi-line messages if needed in the future
   */
  @HostListener('document:keydown.shift.enter', ['$event'])
  handleShiftEnterKey(event: Event) {
    // Currently, we don't support multi-line input, but this is here for future expansion
    // Prevent the regular Enter handler from firing
    event.stopPropagation();
  }

  /**
   * Handle Tab key navigation within the simulation component
   * This improves keyboard accessibility
   */
  @HostListener('document:keydown.tab', ['$event'])
  handleTabKey(event: Event) {
    // Only handle if the simulation component is active and not in loading or complete state
    if (!this.isLoading && !this.isComplete) {
      const target = event.target as HTMLElement;
      const keyboardEvent = event as KeyboardEvent;

      // If we're in the input field and Tab is pressed, move focus to send button
      if (target.classList.contains('simulation-input') && !keyboardEvent.shiftKey) {
        // Find the send button element
        const sendButton = target.closest('.simulation-input-container')?.querySelector('.simulation-send-button');

        if (sendButton && !this.isLoading && this.inputMessage.trim()) {
          event.preventDefault();
          (sendButton as HTMLElement).focus();
        }
      }
    }
  }

  /**
   * Handle Escape key to clear the input field
   * This provides a quick way to cancel typing
   */
  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: Event) {
    // Only handle if the simulation component is active and not in loading or complete state
    if (!this.isLoading && !this.isComplete) {
      const target = event.target as HTMLElement;

      // If we're in the input field or the send button has focus
      if (target.classList.contains('simulation-input') || target.classList.contains('simulation-send-button') || document.activeElement === this.messageInput?.nativeElement) {
        // Clear the input field
        if (this.inputMessage.trim()) {
          this.inputMessage = '';
          this.cdr.detectChanges();
          event.preventDefault();
        }
      }
    }
  }

  getBackgroundColorForAvatar(): string {
    let avatar: number = 0;
    if (this.user?.avatar_id !== undefined) {
      avatar = parseInt(this.user.avatar_id) - 1;
    }
    console.log('test');
    const array = ['#5a36b2', '#007eff', '#3abcd8', '#ec407a', '#0fc461', '#ee8b60', '#f4b60b'];

    return array[avatar % array.length];
  }

  onError(event: any) {
    console.error('Lottie error:', event);
  }

  /**
   * Gets the appropriate placeholder text for the input field based on current state
   */
  getInputPlaceholder(): string {
    if (this.isComplete) {
      return this.translationService.instant('questions.simulation.over');
    } else if (this.isLoading) {
      return this.translationService.instant('questions.simulation.sending');
    } else {
      return this.translationService.instant('questions.simulation.typeMessage');
    }
  }

  /**
   * Starts the loading progress animation
   * This simulates progress during API calls to provide visual feedback
   */
  private startLoadingProgress() {
    // Reset progress
    this.loadingProgress = 0;
    this.showProgressBar = true;

    // Clear any existing timer
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }

    // Simulate progress with a timer
    // The progress will go up to 90% and then wait for the actual completion
    this.progressTimer = setInterval(() => {
      if (this.loadingProgress < 90) {
        // Increment by smaller amounts as we get closer to 90%
        const increment = this.loadingProgress < 30 ? 5 : this.loadingProgress < 60 ? 3 : 1;

        this.loadingProgress += increment;
        this.cdr.detectChanges();
      } else if (this.progressTimer) {
        // Stop at 90%
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    }, 300);

    // Update loading message with a rotating set of messages
    let messageIndex = 0;
    const loadingMessages = [
      this.translationService.instant('questions.simulation.processing'),
      this.translationService.instant('questions.simulation.analyzing'),
      this.translationService.instant('questions.simulation.preparing'),
      this.translationService.instant('questions.simulation.thinking'),
    ];

    const messageTimer = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      this.loadingMessage = loadingMessages[messageIndex];
      this.cdr.detectChanges();
    }, 2000);

    // Store the message timer in the class instance for cleanup
    this.streamingTimer = messageTimer;
  }

  /**
   * Completes the loading progress animation
   */
  private completeLoadingProgress() {
    // Clear the progress timer
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }

    // Set progress to 100%
    this.loadingProgress = 100;
    this.cdr.detectChanges();

    // Hide the progress bar after a short delay
    setTimeout(() => {
      this.showProgressBar = false;
      this.cdr.detectChanges();
    }, 500);

    // Clear the message timer
    if (this.streamingTimer) {
      clearTimeout(this.streamingTimer);
      this.streamingTimer = null;
    }
  }

  /**
   * Sends a user message to the simulation API and processes the response
   */
  async sendMessage() {
    // Don't send empty messages, when loading, or when simulation is complete
    if (!this.inputMessage.trim() || this.isLoading || this.isComplete) {
      return;
    }

    const userMessage = this.inputMessage.trim();
    this.inputMessage = ''; // Clear input field
    this.isLoading = true;

    // Start the loading progress animation
    this.startLoadingProgress();

    // Trigger change detection to update UI immediately
    this.cdr.detectChanges();

    // Add user message to the UI
    this.messages.push({
      content: userMessage,
      isUser: true,
      timestamp: new Date(),
    });

    // Add user message to the allMessages array
    this.allMessages.push({
      role: 'user',
      content: userMessage,
    });

    // Trigger change detection and scroll after user message
    this.cdr.detectChanges();
    this.scrollToBottom(true);

    try {
      // Add a temporary loading message
      const loadingMessageIndex = this.messages.length;
      this.messages.push({
        content: '...',
        isUser: false,
        timestamp: new Date(),
      });

      // Trigger change detection and scroll after loading message
      this.cdr.detectChanges();

      // Multiple scroll attempts to ensure it works
      this.scrollToBottom(true);

      // Additional scroll attempt with a small delay
      setTimeout(() => {
        this.scrollToBottom(true);
      }, 10);

      // Send the message to the API
      let responseContent = '';
      this.finDetected = false;

      // Buffer for accumulating chunks before updating the DOM
      let buffer = '';
      let lastUpdateTime = Date.now();
      const UPDATE_INTERVAL = 100; // Update DOM every 100ms

      // Replace the loading message with an empty content to start
      if (this.messages[loadingMessageIndex].content === '...') {
        this.messages[loadingMessageIndex].content = '';
      }

      for await (const chunk of this.chatbotService.sendSimulationMessage(this.question.module_id, this.question.question_id, this.allMessages)) {
        // IMPORTANT: conserver les retours à la ligne pour un rendu Markdown correct (paragraphes, listes, titres, etc.)
        // On ne supprime plus les '\n'. On normalise juste CRLF -> LF.
        const cleanedChunk = chunk.replace(/\r\n/g, '\n');

        // Add chunk to response content first
        responseContent += cleanedChunk;

        // Check for the termination signal in the accumulated content
        if (responseContent.includes('<FIN>')) {
          // Remove <FIN> from the response content
          responseContent = responseContent.replace('<FIN>', '');

          // Add remaining content to buffer
          buffer += cleanedChunk.replace('<FIN>', ''); // garde les \n
          // Mark as detected (but don't mark as complete yet - wait for stream to finish)
          this.finDetected = true;

          // Continue processing remaining chunks instead of breaking
        }

        // Add to buffer for DOM update (whether FIN was detected or not)
        if (!responseContent.includes('<FIN>')) {
          // Add to buffer for DOM update
          buffer += cleanedChunk; // inclut les \n pour Markdown

          // Only update the DOM periodically to reduce rendering
          const currentTime = Date.now();
          if (currentTime - lastUpdateTime > UPDATE_INTERVAL) {
            this.messages[loadingMessageIndex].content += buffer;
            buffer = '';
            lastUpdateTime = currentTime;

            // Trigger change detection and scroll
            this.cdr.detectChanges();
            this.scrollToBottom();
          }
        }
      }

      // Make sure any remaining buffer is displayed
      if (buffer.trim()) {
        this.messages[loadingMessageIndex].content += buffer;
        this.cdr.detectChanges();
        this.scrollToBottom();
      }

      // Add the bot response to the allMessages array
      if (responseContent) {
        this.allMessages.push({
          role: 'assistant',
          content: responseContent,
        });
      }

      // Now that the stream is complete, mark as complete and emit if FIN was detected
      if (this.finDetected) {
        this.isComplete = true;
        this.cdr.detectChanges();

        // Emit with a small delay to allow UI to update
        setTimeout(() => {
          this.simulationComplete.emit(this.getSimulationState());
        }, 500);
      }
    } catch (error) {
      console.error('Error in simulation:', error);

      // Create error information based on the error type
      let errorMessage = this.translationService.instant('questions.simulation.generalError');
      let errorType: 'network' | 'server' | 'timeout' | 'unknown' = 'unknown';
      let retryable = true;

      // Handle ApiError instances with specific error information
      if (error instanceof ApiError) {
        errorMessage = error.message;
        errorType = error.errorType;

        // Determine if the error is retryable
        retryable = ['network', 'timeout', 'unknown'].includes(error.errorType);

        // Show a toast notification for the error
        this.toastr.error(errorMessage, 'Erreur');
      } else {
        // Generic error handling
        this.toastr.error(this.translationService.instant('questions.simulation.generalError'), this.translationService.instant('common.labels.error'));
      }

      // Create error object
      const chatError: ChatError = {
        message: errorMessage,
        type: errorType,
        retryable: retryable,
      };

      // Replace the loading message with an error message
      if (this.messages[this.messages.length - 1].content === '') {
        this.messages[this.messages.length - 1] = {
          content: errorMessage,
          isUser: false,
          timestamp: new Date(),
          isError: true,
          error: chatError,
        };
      }
    } finally {
      // Complete the loading progress animation
      this.completeLoadingProgress();

      this.isLoading = false;

      // Trigger change detection to update UI immediately
      this.cdr.detectChanges();

      this.scrollToBottom(true); // Immediate scroll to bottom

      // Focus the input field again if simulation is not complete
      if (!this.isComplete) {
        this.focusInputField();
      }
    }
  }

  /**
   * Retry sending the last user message after an error
   */
  retryLastMessage() {
    // Find the last user message
    const lastUserMessageIndex = [...this.messages].reverse().findIndex((m) => m.isUser);

    if (lastUserMessageIndex >= 0) {
      // Get the actual index in the array
      const actualIndex = this.messages.length - 1 - lastUserMessageIndex;
      const lastUserMessage = this.messages[actualIndex];

      // Remove all messages after the last user message (including error messages)
      this.messages = this.messages.slice(0, actualIndex + 1);

      // Also remove the corresponding messages from allMessages
      // Find the index of the last user message in allMessages
      const lastUserAllMessageIndex = [...this.allMessages].reverse().findIndex((m) => m.role === 'user');
      if (lastUserAllMessageIndex >= 0) {
        const actualAllIndex = this.allMessages.length - 1 - lastUserAllMessageIndex;
        this.allMessages = this.allMessages.slice(0, actualAllIndex + 1);
      }

      // Set the input message to the last user message and send it
      this.inputMessage = lastUserMessage.content;
      this.sendMessage();
    }
  }

  /**
   * Gets the current state of the simulation
   * Useful for parent components to check status
   */
  getSimulationState(): SimulationState {
    // Find the last error message if any
    const lastErrorMessage = [...this.messages].reverse().find((m) => m.isError);

    return {
      messages: [...this.messages],
      isLoading: this.isLoading,
      isComplete: this.isComplete,
      error: lastErrorMessage?.error,
    };
  }

  /**
   * Checks if the FIN signal has been detected
   */
  isFinDetected(): boolean {
    return this.finDetected;
  }

  /**
   * TrackBy function for the ngFor directive to optimize rendering
   * This helps Angular identify which items have changed
   */
  trackByFn(index: number, item: ChatMessage): number {
    // Use timestamp as unique identifier, fallback to index
    return item.timestamp?.getTime() ?? index;
  }
}
